import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { USER_ROLES, USER_ROLE_LABELS } from '../utils/constants';

const ViewAsContext = createContext(null);

export const ViewAsProvider = ({ children }) => {
  const { user } = useAuth();
  const [viewingRole, setViewingRole] = useState(null);

  // Reset viewing role when user changes or logs out
  useEffect(() => {
    if (!user) {
      setViewingRole(null);
    }
  }, [user]);

  // Get the role hierarchy - roles that current user can view as
  const getAvailableRoles = useMemo(() => {
    if (!user?.role) return [];

    const roleHierarchy = {
      [USER_ROLES.SUPER_ADMIN]: [
        USER_ROLES.EVENT_ADMIN,
        USER_ROLES.FRONT_DESK,
        USER_ROLES.USHER,
        USER_ROLES.ATTENDEE
      ],
      [USER_ROLES.EVENT_ADMIN]: [
        USER_ROLES.FRONT_DESK,
        USER_ROLES.USHER,
        USER_ROLES.ATTENDEE
      ],
      [USER_ROLES.FRONT_DESK]: [
        USER_ROLES.USHER,
        USER_ROLES.ATTENDEE
      ],
      [USER_ROLES.USHER]: [
        USER_ROLES.ATTENDEE
      ],
      [USER_ROLES.ATTENDEE]: []
    };

    return roleHierarchy[user.role] || [];
  }, [user?.role]);

  // Get current effective role (viewing role or actual role)
  const effectiveRole = viewingRole || user?.role;

  // Create effective user object with viewing role
  const effectiveUser = useMemo(() => {
    if (!user) return null;
    
    return {
      ...user,
      role: effectiveRole
    };
  }, [user, effectiveRole]);

  // Check if user can view as a specific role
  const canViewAs = (role) => {
    return getAvailableRoles.includes(role);
  };

  // Switch to viewing as a specific role
  const switchToRole = (role) => {
    if (canViewAs(role)) {
      setViewingRole(role);
    }
  };

  // Exit view as mode and return to actual role
  const exitViewAs = () => {
    setViewingRole(null);
  };

  // Check if currently in view as mode
  const isViewingAs = viewingRole !== null;

  const value = {
    // Current state
    user, // Original user object
    effectiveUser, // User object with effective role
    viewingRole,
    effectiveRole,
    isViewingAs,
    
    // Available roles
    availableRoles: getAvailableRoles,
    
    // Actions
    canViewAs,
    switchToRole,
    exitViewAs,
    
    // Helper to get role label
    getRoleLabel: (role) => USER_ROLE_LABELS[role] || role,
  };

  return (
    <ViewAsContext.Provider value={value}>
      {children}
    </ViewAsContext.Provider>
  );
};

export const useViewAs = () => {
  const context = useContext(ViewAsContext);
  if (!context) {
    throw new Error('useViewAs must be used within a ViewAsProvider');
  }
  return context;
};
